"use client";

import { useState, useEffect } from "react";
import type { ErrorMessage, Script } from "@/types/script";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { env } from "@/env";
import { useToast } from "@/components/ui/use-toast";
import { formatTimeAgo } from "@/lib/format-time-ago";
import Image from "next/image";
import { Pencil, Trash2 } from "lucide-react";
import {
  Modal,
  ModalTrigger,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "@/components/modal";
import EditScriptForm from "@/components/script/edit-script-modal";
import { buildImageLink } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { AspectRatio } from "@radix-ui/react-aspect-ratio";

interface ScriptsDataTableProps {
  scripts: Script[];
}

export default function ScriptsDataTable({ scripts }: ScriptsDataTableProps) {
  const { user, accessToken } = useAuth();
  const [data, setData] = useState<Script[]>(scripts);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { toast } = useToast();
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);

  const columns: ColumnDef<Script>[] = [
    {
      accessorKey: "image",
      header: "Image",
      cell: ({ row }) => (
        <AspectRatio ratio={16 / 9} className="overflow-hidden">
          <Image
            src={buildImageLink(row.original.images[0]!)}
            alt={row.original.title}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            fill
          />
        </AspectRatio>
      ),
    },
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ row }) => formatTimeAgo(row.original.created_at),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <Modal>
            <ModalTrigger asChild>
              <Button
                variant="secondary"
                className="gap-1"
                size="sm"
                onClick={() => setSelectedScript(row.original)}
              >
                Edit <Pencil size={12} />
              </Button>
            </ModalTrigger>
            <ModalContent className="max-w-3xl">
              <ModalHeader>
                <ModalTitle>Edit Script</ModalTitle>
              </ModalHeader>
              <ModalBody>
                {selectedScript && <EditScriptForm script={selectedScript} />}
              </ModalBody>
            </ModalContent>
          </Modal>
          <Button
            variant="destructive"
            className="gap-1"
            size="sm"
            onClick={() => handleDelete(row.original.slug)}
          >
            Delete <Trash2 size={12} />
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(
          `${env.NEXT_PUBLIC_API_URL}/user/${user!.username}/posts?page=${pageIndex + 1}&limit=${pageSize}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch scripts");
        }
        if (response.status === 429) {
          throw new Error("Rate limit exceeded. Please try again later.");
        }

        const result = await response.json();
        setData(result.data.posts.result);
      } catch (error) {
        toast({
          title: "Error",
          description: (error as ErrorMessage).message,
          variant: "destructive",
        });
      }
    }

    fetchData();
  }, [pageIndex, pageSize, accessToken, toast]);

  const handleDelete = async (slug: string) => {
    try {
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/post/${slug}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete script");
      }

      setData((prevData) => prevData.filter((script) => script.slug !== slug));
      toast({
        title: "Success",
        description: "Script deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: (error as ErrorMessage).message,
        variant: "destructive",
      });
    }
  };

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(data.length / pageSize),
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize })
          : updater;
      setPageIndex(newState.pageIndex);
      setPageSize(newState.pageSize);
    },
  });

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
        <span className="text-sm text-muted-foreground">
          Page {pageIndex + 1} of {table.getPageCount()}
        </span>
      </div>
    </div>
  );
}
